import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { MdUpload, MdAdd, MdDelete } from 'react-icons/md';
import { Secoes, SiteEmpresaDto, obterUrlImagemPreview, uploadImagemHero } from '../../services/meuSiteService';

const AZUL = '#1F467E';
const BORDA = '#e2e8f0';
const FUNDO = '#f5f7fa';

const Card = styled.div`
    background: #fff;
    border: 1px solid ${BORDA};
    border-radius: 8px;
    padding: 20px 24px;
    margin-bottom: 16px;
`;

const CardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
`;

const CardTitle = styled.h3`
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
`;

const Toggle = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    font-size: 13px;
    color: #64748b;
    user-select: none;
`;

const ToggleInput = styled.input`
    width: 36px;
    height: 20px;
    cursor: pointer;
    accent-color: ${AZUL};
`;

const Field = styled.div`
    margin-bottom: 12px;
`;

const FieldLabel = styled.label`
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    margin-bottom: 4px;
`;

const Input = styled.input`
    width: 100%;
    height: 36px;
    border: 1px solid ${BORDA};
    border-radius: 6px;
    padding: 0 10px;
    font-size: 13px;
    color: #374151;
    box-sizing: border-box;
    &:focus { outline: 2px solid ${AZUL}; }
    &:disabled { background: #f1f5f9; color: #94a3b8; cursor: not-allowed; }
`;

const Textarea = styled.textarea`
    width: 100%;
    border: 1px solid ${BORDA};
    border-radius: 6px;
    padding: 8px 10px;
    font-size: 13px;
    color: #374151;
    resize: vertical;
    box-sizing: border-box;
    &:focus { outline: 2px solid ${AZUL}; }
`;

const ItemCard = styled.div`
    background: ${FUNDO};
    border: 1px solid ${BORDA};
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 10px;
    position: relative;
`;

const RemoveBtn = styled.button`
    position: absolute;
    top: 8px;
    right: 8px;
    background: none;
    border: none;
    color: #ef4444;
    cursor: pointer;
    padding: 2px;
    display: flex;
    align-items: center;
`;

const AddBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: 1px dashed #94a3b8;
    border-radius: 6px;
    background: none;
    color: #64748b;
    font-size: 13px;
    cursor: pointer;
    margin-top: 4px;
    &:hover { border-color: ${AZUL}; color: ${AZUL}; }
`;

const UploadBtn = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 7px 14px;
    border: 1px solid ${AZUL};
    border-radius: 6px;
    background: #fff;
    color: ${AZUL};
    font-size: 13px;
    cursor: pointer;
    &:hover { background: #f0f4fa; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const HeroPreview = styled.img`
    max-width: 200px;
    max-height: 120px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid ${BORDA};
    display: block;
    margin-bottom: 8px;
`;

const BarraSalvar = styled.div`
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    justify-content: flex-end;
    padding: 8px 0 12px;
    margin-bottom: 8px;
    background: ${FUNDO};
`;

const SaveBtn = styled.button`
    padding: 10px 24px;
    background: ${AZUL};
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    &:hover { background: #163560; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const NoteInput = styled.input`
    width: 60px;
    height: 36px;
    border: 1px solid ${BORDA};
    border-radius: 6px;
    padding: 0 8px;
    font-size: 13px;
    color: #374151;
    &:focus { outline: 2px solid ${AZUL}; }
`;

interface Props {
    site: SiteEmpresaDto;
    secoes: Secoes;
    onSecoesChange: (s: Secoes) => void;
    onSave: () => Promise<void>;
    saving: boolean;
}

function extrairMensagem(err: any): string {
    return err?.response?.data?.mensagem
        || err?.response?.data?.Mensagem
        || 'Erro ao realizar operação.';
}

export function AbaSecoes({ site, secoes, onSecoesChange, onSave, saving }: Props) {
    const heroInputRef = useRef<HTMLInputElement>(null);
    const [uploadingHero, setUploadingHero] = useState(false);

    const [heroImgUrl, setHeroImgUrl] = useState<string | null>(null);

    useEffect(() => {
        let ativo = true;
        if (!secoes.hero.imagemArquivoId) {
            setHeroImgUrl(null);
            return;
        }
        obterUrlImagemPreview(secoes.hero.imagemArquivoId).then(url => {
            if (ativo) setHeroImgUrl(url);
        });
        return () => { ativo = false; };
    }, [secoes.hero.imagemArquivoId]);

    const handleHeroUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploadingHero(true);
        try {
            const res = await uploadImagemHero(file);
            onSecoesChange({ ...secoes, hero: { ...secoes.hero, imagemArquivoId: res.data.arquivoId } });
            toast.success('Imagem do hero atualizada.');
        } catch (err) {
            toast.error(extrairMensagem(err));
        } finally {
            setUploadingHero(false);
            if (heroInputRef.current) heroInputRef.current.value = '';
        }
    };

    const addComoFuncionaItem = () => {
        if (secoes.comoFunciona.itens.length >= 6) return;
        onSecoesChange({
            ...secoes,
            comoFunciona: {
                ...secoes.comoFunciona,
                itens: [...secoes.comoFunciona.itens, { titulo: '', texto: '' }],
            },
        });
    };

    const removeComoFuncionaItem = (i: number) => {
        onSecoesChange({
            ...secoes,
            comoFunciona: {
                ...secoes.comoFunciona,
                itens: secoes.comoFunciona.itens.filter((_, idx) => idx !== i),
            },
        });
    };

    const updateComoFuncionaItem = (i: number, field: 'titulo' | 'texto', value: string) => {
        const itens = [...secoes.comoFunciona.itens];
        itens[i] = { ...itens[i], [field]: value };
        onSecoesChange({ ...secoes, comoFunciona: { ...secoes.comoFunciona, itens } });
    };

    const addDepoimento = () => {
        if (secoes.depoimentos.itens.length >= 6) return;
        onSecoesChange({
            ...secoes,
            depoimentos: {
                ...secoes.depoimentos,
                itens: [...secoes.depoimentos.itens, { nome: '', texto: '', nota: 5 }],
            },
        });
    };

    const removeDepoimento = (i: number) => {
        onSecoesChange({
            ...secoes,
            depoimentos: {
                ...secoes.depoimentos,
                itens: secoes.depoimentos.itens.filter((_, idx) => idx !== i),
            },
        });
    };

    const updateDepoimento = (i: number, field: 'nome' | 'texto' | 'nota', value: any) => {
        const itens = [...secoes.depoimentos.itens];
        itens[i] = { ...itens[i], [field]: value };
        onSecoesChange({ ...secoes, depoimentos: { ...secoes.depoimentos, itens } });
    };

    const addFaq = () => {
        if (secoes.faq.itens.length >= 10) return;
        onSecoesChange({
            ...secoes,
            faq: { ...secoes.faq, itens: [...secoes.faq.itens, { pergunta: '', resposta: '' }] },
        });
    };

    const removeFaq = (i: number) => {
        onSecoesChange({
            ...secoes,
            faq: { ...secoes.faq, itens: secoes.faq.itens.filter((_, idx) => idx !== i) },
        });
    };

    const updateFaq = (i: number, field: 'pergunta' | 'resposta', value: string) => {
        const itens = [...secoes.faq.itens];
        itens[i] = { ...itens[i], [field]: value };
        onSecoesChange({ ...secoes, faq: { ...secoes.faq, itens } });
    };

    const atualizarSeo = (campo: 'titulo' | 'descricao', valor: string) =>
        onSecoesChange({ ...secoes, seo: { titulo: '', descricao: '', ...secoes.seo, [campo]: valor } });

    return (
        <div>
            <BarraSalvar>
                <SaveBtn onClick={onSave} disabled={saving}>
                    {saving ? 'Salvando...' : 'Salvar seções'}
                </SaveBtn>
            </BarraSalvar>
            <Card>
                <CardHeader>
                    <CardTitle>Título no navegador e buscadores</CardTitle>
                </CardHeader>
                <Field>
                    <FieldLabel>Título da aba do navegador</FieldLabel>
                    <Input
                        value={secoes.seo?.titulo || ''}
                        placeholder="Ex.: Sanford Motos, aluguel de motos em Fortaleza"
                        onChange={e => atualizarSeo('titulo', e.target.value)}
                    />
                </Field>
                <Field>
                    <FieldLabel>Descrição para buscadores (Google)</FieldLabel>
                    <Textarea
                        rows={2}
                        value={secoes.seo?.descricao || ''}
                        placeholder="Deixe em branco para usar o subtítulo do banner principal"
                        onChange={e => atualizarSeo('descricao', e.target.value)}
                    />
                </Field>
            </Card>
            <Card>
                <CardHeader>
                    <CardTitle>Hero (Banner principal)</CardTitle>
                    <Toggle>
                        <ToggleInput
                            type="checkbox"
                            checked={secoes.hero.visivel}
                            onChange={e => onSecoesChange({ ...secoes, hero: { ...secoes.hero, visivel: e.target.checked } })}
                        />
                        Exibir no site
                    </Toggle>
                </CardHeader>
                <Field>
                    <FieldLabel>Título</FieldLabel>
                    <Input value={secoes.hero.titulo} onChange={e => onSecoesChange({ ...secoes, hero: { ...secoes.hero, titulo: e.target.value } })} />
                </Field>
                <Field>
                    <FieldLabel>Subtítulo</FieldLabel>
                    <Input value={secoes.hero.subtitulo} onChange={e => onSecoesChange({ ...secoes, hero: { ...secoes.hero, subtitulo: e.target.value } })} />
                </Field>
                <Field>
                    <Toggle>
                        <ToggleInput
                            type="checkbox"
                            checked={secoes.hero.badgeVisivel !== false}
                            onChange={e => onSecoesChange({ ...secoes, hero: { ...secoes.hero, badgeVisivel: e.target.checked } })}
                        />
                        Exibir selo de destaque
                    </Toggle>
                </Field>
                <Field>
                    <FieldLabel>Título do selo</FieldLabel>
                    <Input
                        disabled={secoes.hero.badgeVisivel === false}
                        value={secoes.hero.badgeTitulo || ''}
                        placeholder="Pronta entrega"
                        onChange={e => onSecoesChange({ ...secoes, hero: { ...secoes.hero, badgeTitulo: e.target.value } })}
                    />
                </Field>
                <Field>
                    <FieldLabel>Texto do selo</FieldLabel>
                    <Input
                        disabled={secoes.hero.badgeVisivel === false}
                        value={secoes.hero.badgeTexto || ''}
                        placeholder="Retirada no mesmo dia"
                        onChange={e => onSecoesChange({ ...secoes, hero: { ...secoes.hero, badgeTexto: e.target.value } })}
                    />
                </Field>
                <Field>
                    <FieldLabel>Imagem de fundo</FieldLabel>
                    {heroImgUrl && <HeroPreview src={heroImgUrl} alt="Hero" />}
                    <UploadBtn type="button" disabled={uploadingHero} onClick={() => heroInputRef.current?.click()}>
                        <MdUpload size={15} />
                        {uploadingHero ? 'Enviando...' : 'Enviar imagem'}
                    </UploadBtn>
                    <input ref={heroInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleHeroUpload} />
                </Field>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Frota</CardTitle>
                    <Toggle>
                        <ToggleInput type="checkbox" checked={secoes.frota.visivel} onChange={e => onSecoesChange({ ...secoes, frota: { ...secoes.frota, visivel: e.target.checked } })} />
                        Exibir no site
                    </Toggle>
                </CardHeader>
                <Field>
                    <FieldLabel>Título da seção</FieldLabel>
                    <Input value={secoes.frota.titulo} onChange={e => onSecoesChange({ ...secoes, frota: { ...secoes.frota, titulo: e.target.value } })} />
                </Field>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Como funciona</CardTitle>
                    <Toggle>
                        <ToggleInput type="checkbox" checked={secoes.comoFunciona.visivel} onChange={e => onSecoesChange({ ...secoes, comoFunciona: { ...secoes.comoFunciona, visivel: e.target.checked } })} />
                        Exibir no site
                    </Toggle>
                </CardHeader>
                <Field>
                    <FieldLabel>Título da seção</FieldLabel>
                    <Input value={secoes.comoFunciona.titulo} onChange={e => onSecoesChange({ ...secoes, comoFunciona: { ...secoes.comoFunciona, titulo: e.target.value } })} />
                </Field>
                {secoes.comoFunciona.itens.map((item, i) => (
                    <ItemCard key={i}>
                        <RemoveBtn type="button" onClick={() => removeComoFuncionaItem(i)}><MdDelete size={16} /></RemoveBtn>
                        <Field>
                            <FieldLabel>Título do item {i + 1}</FieldLabel>
                            <Input value={item.titulo} onChange={e => updateComoFuncionaItem(i, 'titulo', e.target.value)} />
                        </Field>
                        <Field>
                            <FieldLabel>Texto</FieldLabel>
                            <Input value={item.texto} onChange={e => updateComoFuncionaItem(i, 'texto', e.target.value)} />
                        </Field>
                    </ItemCard>
                ))}
                {secoes.comoFunciona.itens.length < 6 && (
                    <AddBtn type="button" onClick={addComoFuncionaItem}><MdAdd size={15} /> Adicionar item</AddBtn>
                )}
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Depoimentos</CardTitle>
                    <Toggle>
                        <ToggleInput type="checkbox" checked={secoes.depoimentos.visivel} onChange={e => onSecoesChange({ ...secoes, depoimentos: { ...secoes.depoimentos, visivel: e.target.checked } })} />
                        Exibir no site
                    </Toggle>
                </CardHeader>
                {secoes.depoimentos.itens.map((dep, i) => (
                    <ItemCard key={i}>
                        <RemoveBtn type="button" onClick={() => removeDepoimento(i)}><MdDelete size={16} /></RemoveBtn>
                        <Field>
                            <FieldLabel>Nome</FieldLabel>
                            <Input value={dep.nome} onChange={e => updateDepoimento(i, 'nome', e.target.value)} />
                        </Field>
                        <Field>
                            <FieldLabel>Texto</FieldLabel>
                            <Input value={dep.texto} onChange={e => updateDepoimento(i, 'texto', e.target.value)} />
                        </Field>
                        <Field>
                            <FieldLabel>Nota (1 a 5)</FieldLabel>
                            <NoteInput type="number" min={1} max={5} value={dep.nota} onChange={e => updateDepoimento(i, 'nota', Math.min(5, Math.max(1, Number(e.target.value))))} />
                        </Field>
                    </ItemCard>
                ))}
                {secoes.depoimentos.itens.length < 6 && (
                    <AddBtn type="button" onClick={addDepoimento}><MdAdd size={15} /> Adicionar depoimento</AddBtn>
                )}
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>FAQ</CardTitle>
                    <Toggle>
                        <ToggleInput type="checkbox" checked={secoes.faq.visivel} onChange={e => onSecoesChange({ ...secoes, faq: { ...secoes.faq, visivel: e.target.checked } })} />
                        Exibir no site
                    </Toggle>
                </CardHeader>
                {secoes.faq.itens.map((faq, i) => (
                    <ItemCard key={i}>
                        <RemoveBtn type="button" onClick={() => removeFaq(i)}><MdDelete size={16} /></RemoveBtn>
                        <Field>
                            <FieldLabel>Pergunta</FieldLabel>
                            <Input value={faq.pergunta} onChange={e => updateFaq(i, 'pergunta', e.target.value)} />
                        </Field>
                        <Field>
                            <FieldLabel>Resposta</FieldLabel>
                            <Textarea rows={2} value={faq.resposta} onChange={e => updateFaq(i, 'resposta', e.target.value)} />
                        </Field>
                    </ItemCard>
                ))}
                {secoes.faq.itens.length < 10 && (
                    <AddBtn type="button" onClick={addFaq}><MdAdd size={15} /> Adicionar pergunta</AddBtn>
                )}
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Sobre nós</CardTitle>
                    <Toggle>
                        <ToggleInput type="checkbox" checked={secoes.sobre.visivel} onChange={e => onSecoesChange({ ...secoes, sobre: { ...secoes.sobre, visivel: e.target.checked } })} />
                        Exibir no site
                    </Toggle>
                </CardHeader>
                <Field>
                    <FieldLabel>Texto sobre a empresa</FieldLabel>
                    <Textarea rows={4} value={secoes.sobre.texto} onChange={e => onSecoesChange({ ...secoes, sobre: { ...secoes.sobre, texto: e.target.value } })} />
                </Field>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Localização</CardTitle>
                    <Toggle>
                        <ToggleInput type="checkbox" checked={secoes.localizacao.visivel} onChange={e => onSecoesChange({ ...secoes, localizacao: { ...secoes.localizacao, visivel: e.target.checked } })} />
                        Exibir no site
                    </Toggle>
                </CardHeader>
                <Field>
                    <FieldLabel>Endereço</FieldLabel>
                    <Input value={secoes.localizacao.endereco} onChange={e => onSecoesChange({ ...secoes, localizacao: { ...secoes.localizacao, endereco: e.target.value } })} />
                </Field>
                <Field>
                    <FieldLabel>Link do Google Maps</FieldLabel>
                    <Input value={secoes.localizacao.mapsUrl} onChange={e => onSecoesChange({ ...secoes, localizacao: { ...secoes.localizacao, mapsUrl: e.target.value } })} placeholder="https://maps.google.com/..." />
                </Field>
                <Field>
                    <FieldLabel>Telefone</FieldLabel>
                    <Input value={secoes.localizacao.telefone} onChange={e => onSecoesChange({ ...secoes, localizacao: { ...secoes.localizacao, telefone: e.target.value } })} />
                </Field>
                <Field>
                    <FieldLabel>E-mail</FieldLabel>
                    <Input type="email" value={secoes.localizacao.email} onChange={e => onSecoesChange({ ...secoes, localizacao: { ...secoes.localizacao, email: e.target.value } })} />
                </Field>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>WhatsApp flutuante</CardTitle>
                    <Toggle>
                        <ToggleInput type="checkbox" checked={secoes.whatsappFlutuante.visivel} onChange={e => onSecoesChange({ ...secoes, whatsappFlutuante: { ...secoes.whatsappFlutuante, visivel: e.target.checked } })} />
                        Exibir no site
                    </Toggle>
                </CardHeader>
                <Field>
                    <FieldLabel>Mensagem padrão ao abrir conversa</FieldLabel>
                    <Input value={secoes.whatsappFlutuante.mensagemPadrao} onChange={e => onSecoesChange({ ...secoes, whatsappFlutuante: { ...secoes.whatsappFlutuante, mensagemPadrao: e.target.value } })} />
                </Field>
            </Card>
        </div>
    );
}
